import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

/**
 * Learner-facing progress, backed by two read-only SECURITY DEFINER RPCs
 * (20260728000001_learner_progress_rpcs.sql). Both enforce
 * "caller is the subject, or an admin" server-side.
 *
 * WHY AN RPC RATHER THAN CLIENT QUERIES: within-module progress cannot be read
 * from module_progress -- that table only records COMPLETIONS (every row in
 * production has completed_at set). It has to be derived from passed quiz
 * attempts, satisfied assignment submissions and homework submissions, which
 * would otherwise mean several round-trips per module.
 *
 * The `as any` casts are here because these RPCs are not yet in the generated
 * types.ts (Lovable regenerates that file, so hand-editing it would be lost).
 * They are deliberately confined to this module rather than spread across call
 * sites, so there is exactly one place to delete them once types catch up.
 */

export interface CurriculumProgressSummary {
  curriculumId: string;
  curriculumTitle: string;
  totalModules: number;
  completedModules: number;
  /** 0-100 across the whole curriculum, including partial within-module credit. */
  overallPercent: number;
  /** For cohort learners, the modules an instructor has unlocked. */
  assignedModules: number;
  assignedCompleted: number;
  assignedPercent: number;
  /** True when the learner is in a gated cohort for a course in this curriculum. */
  isCohortGated: boolean;
  /** Assigned, open to them, and not finished — what they can act on now. */
  outstandingModules: number;
  /** Every item done but the module was never marked complete. */
  readyToComplete: number;
}

export interface ModuleProgressRow {
  curriculumId: string;
  curriculumTitle: string;
  courseId: string;
  courseTitle: string;
  moduleId: string;
  moduleTitle: string;
  sequenceOrder: number;
  isAssigned: boolean;
  isUnlocked: boolean;
  isComplete: boolean;
  itemsTotal: number;
  itemsDone: number;
  fraction: number;
}

/** The state a module is in, from the learner's point of view. */
export type ModuleState =
  | "complete"
  | "ready" // all items done, not marked complete
  | "in_progress" // some items done
  | "available" // unlocked, nothing done
  | "locked" // sequentially locked
  | "not_assigned"; // cohort has not unlocked it yet

export function moduleState(row: ModuleProgressRow): ModuleState {
  if (row.isComplete) return "complete";
  if (!row.isAssigned) return "not_assigned";
  if (!row.isUnlocked) return "locked";
  if (row.itemsTotal > 0 && row.itemsDone === row.itemsTotal) return "ready";
  if (row.itemsDone > 0) return "in_progress";
  return "available";
}

export const MODULE_STATE_LABEL: Record<ModuleState, string> = {
  complete: "Complete",
  ready: "Ready to mark complete",
  in_progress: "In progress",
  available: "Not started",
  locked: "Locked",
  not_assigned: "Not yet assigned",
};

/** Summary for every curriculum the learner is enrolled in. */
export function useLearnerCurriculumProgress() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["learner-curriculum-progress", user?.id],
    enabled: !!user?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<CurriculumProgressSummary[]> => {
      if (!user?.id) return [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)(
        "get_learner_curriculum_summary",
        { _user_id: user.id }
      );
      if (error) throw error;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((data ?? []) as any[]).map((r) => ({
        curriculumId: r.curriculum_id,
        curriculumTitle: r.curriculum_title,
        totalModules: r.total_modules ?? 0,
        completedModules: r.completed_modules ?? 0,
        overallPercent: r.overall_percent ?? 0,
        assignedModules: r.assigned_modules ?? 0,
        assignedCompleted: r.assigned_completed ?? 0,
        assignedPercent: r.assigned_percent ?? 0,
        isCohortGated: !!r.is_cohort_gated,
        outstandingModules: r.outstanding_modules ?? 0,
        readyToComplete: r.ready_to_complete ?? 0,
      }));
    },
  });
}

/** Per-module detail. Pass a curriculumId to scope it to one programme. */
export function useLearnerModuleProgress(curriculumId?: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["learner-module-progress", user?.id, curriculumId ?? "all"],
    enabled: !!user?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<ModuleProgressRow[]> => {
      if (!user?.id) return [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)(
        "get_learner_module_progress",
        { _user_id: user.id, _curriculum_id: curriculumId ?? null }
      );
      if (error) throw error;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((data ?? []) as any[]).map((r) => ({
        curriculumId: r.curriculum_id,
        curriculumTitle: r.curriculum_title,
        courseId: r.course_id,
        courseTitle: r.course_title,
        moduleId: r.module_id,
        moduleTitle: r.module_title,
        sequenceOrder: r.sequence_order ?? 0,
        isAssigned: !!r.is_assigned,
        isUnlocked: !!r.is_unlocked,
        isComplete: !!r.is_complete,
        itemsTotal: r.items_total ?? 0,
        itemsDone: r.items_done ?? 0,
        fraction: Number(r.fraction ?? 0),
      }));
    },
  });
}

/** Query keys to invalidate whenever progress could have changed. */
export const LEARNER_PROGRESS_KEYS = [
  ["learner-curriculum-progress"],
  ["learner-module-progress"],
] as const;
